import React from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import MobileLoginForm from "@/components/auth/mobile/MobileLoginForm";

/**
 * ==========================================
 * AFFILIATE PROGRAM - Login Page
 * ==========================================
 * 
 * Trang đăng nhập, tích hợp xử lý mã giới thiệu (referral code).
 * 
 * Các chức năng chính:
 * - Kiểm tra và lưu mã giới thiệu từ URL hoặc cookie khi load trang
 * - Lưu mã giới thiệu vào localStorage để xử lý sau khi đăng nhập thành công
 * - Xử lý chuyển hướng sau đăng nhập
 * 
 * Quy trình:
 * 1. Kiểm tra URL và cookie để lấy referral code
 * 2. Lưu mã giới thiệu vào localStorage dưới key 'pendingReferralCode'
 * 3. Sau khi đăng nhập thành công, AuthCallback.tsx sẽ xử lý ghi nhận giới thiệu
 */

// Desktop version components
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { supabase } from "@/lib/supabase";
import { Mail, Lock, Eye, EyeOff, TrendingUp, ChartBar } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { ImageWithFallback } from "@/components/common/ImageWithFallback";
import { useLogoPreloader } from "@/hooks/useLogoPreloader";
import { toast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { resolveReferralCode } from "@/utils/referralUtils";
import { TypingEffect } from "@/components/bot-builder/TypingEffect";
import ForgotPasswordDialog from "@/components/auth/ForgotPasswordDialog";

// Flag to control Google authentication visibility - can be moved to environment variables later
const ENABLE_GOOGLE_AUTH = false;

const formSchema = z.object({
  email: z.string().email({ message: "Invalid email address" }),
  password: z.string().min(6, { message: "Password must be at least 6 characters" }),
});

const DesktopLogin = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = React.useState(false);
  const [loginError, setLoginError] = React.useState<string | null>(null);
  const [showPassword, setShowPassword] = React.useState(false);
  const [animationStage, setAnimationStage] = React.useState(0);
  const [forgotPasswordOpen, setForgotPasswordOpen] = React.useState(false);
  
  // Preload logos as soon as component mounts
  useLogoPreloader();
  
  // Animation sequence control
  React.useEffect(() => {
    const sequence = [
      { stage: 1, delay: 500 },    // Show title
      { stage: 2, delay: 1000 },   // Start typing description
      { stage: 3, delay: 3500 },   // Show chart icon
      { stage: 4, delay: 4000 },   // Animate decorative elements
    ];

    let timer: ReturnType<typeof setTimeout>;
    let currentIndex = 0;

    const runNextAnimation = () => {
      if (currentIndex < sequence.length) {
        const { stage, delay } = sequence[currentIndex];
        setAnimationStage(stage);
        
        timer = setTimeout(() => {
          currentIndex++;
          runNextAnimation();
        }, delay);
      }
    };

    runNextAnimation();

    return () => clearTimeout(timer);
  }, []);
  
  // Check for referral codes on page load - store them for later use
  React.useEffect(() => {
    const refCode = resolveReferralCode();
    if (refCode) {
      // Store for later attribution after login
      localStorage.setItem('pendingReferralCode', refCode);
    }
  }, []);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    setLoginError(null); // Clear previous errors
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: values.email,
        password: values.password,
      });
      
      if (error) {
        // Handle specific error cases with clear messages
        if (error.message.includes("Invalid login credentials") || 
            error.message.includes("Invalid email or password")) {
          setLoginError("Incorrect email or password. Please try again.");
        } else {
          setLoginError(error.message);
        }
        return;
      }
      
      // Check if there's a pending referral code to process
      const pendingReferralCode = localStorage.getItem('pendingReferralCode');
      if (pendingReferralCode && data.user) {
        // We could process this here if needed or in the callback page
        // Clear the stored code after processing
        localStorage.removeItem('pendingReferralCode');
      }
      
      // The toast is now managed by AuthContext.tsx - Don't duplicate it here
      navigate("/");
    } catch (error) {
      setLoginError("An unexpected error occurred. Please try again.");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setLoginError(null);
    
    try {
      // Ensure we use the full origin for the redirect URL
      const redirectUrl = `${window.location.origin}/auth/callback`;
      console.log("Google login redirect URL:", redirectUrl);
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
        }
      });
      
      if (error) {
        setLoginError(error.message);
        console.error("Google OAuth error:", error);
      }
    } catch (error) {
      setLoginError("Failed to connect with Google. Please try again.");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-gradient-to-br from-botscheme-royal-purple/10 via-background to-botscheme-emerald-green/10">
      <div className="relative w-full max-w-5xl px-4">
        {/* Decorative elements */}
        <div className="absolute -z-10 h-56 w-56 -left-10 top-10 rounded-full bg-botscheme-royal-purple/20 blur-3xl"></div>
        <div className="absolute right-0 top-1/2 -z-10 h-72 w-72 rounded-full bg-botscheme-emerald-green/20 blur-3xl"></div>
        
        <div className="flex w-full flex-col-reverse overflow-hidden rounded-2xl bg-white/5 backdrop-blur-xl shadow-xl border border-white/10 lg:flex-row">
          {/* Form container */}
          <div className="w-full p-8 lg:w-1/2 lg:p-12">
            <div className="flex flex-col h-full justify-center">
              <div className="mb-6 flex justify-center lg:justify-start">
                <div className="w-64 h-24 relative">
                  <ImageWithFallback 
                    src="https://lbvhywxvthwgiebjsrlr.supabase.co/storage/v1/object/public/logoexchangeplatformurl//logo%20trade%20bot%20365.png" 
                    alt="Trade Bot 365 Logo" 
                    className="w-full h-full object-contain"
                    loading="eager"
                    skeletonClassName="bg-muted/50"
                  />
                </div>
              </div>
              
              <div className="space-y-6 animate-fade-in">
                <div className="text-center lg:text-left">
                  <h1 className="text-2xl font-bold lg:text-3xl">Welcome Back</h1>
                  <p className="mt-2 text-muted-foreground">Sign in to your account to continue</p>
                </div>
                
                {loginError && (
                  <Alert variant="destructive" className="animate-fade-in border-destructive/30 bg-destructive/10">
                    <AlertDescription>{loginError}</AlertDescription>
                  </Alert>
                )}
                
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <div className="relative">
                            <Mail className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                            <FormControl>
                              <Input 
                                placeholder="you@example.com" 
                                className="pl-10 bg-white/5 border-white/10 focus:border-botscheme-ocean-blue transition-all" 
                                {...field} 
                              />
                            </FormControl>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Password</FormLabel>
                          <div className="relative">
                            <Lock className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                            <FormControl>
                              <Input 
                                type={showPassword ? "text" : "password"} 
                                placeholder="••••••••" 
                                className="pl-10 bg-white/5 border-white/10 focus:border-botscheme-ocean-blue transition-all" 
                                {...field} 
                              />
                            </FormControl>
                            <button 
                              type="button" 
                              className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground transition-colors"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? (
                                <EyeOff className="h-5 w-5" />
                              ) : (
                                <Eye className="h-5 w-5" />
                              )}
                            </button>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="flex items-center justify-between mt-2 space-x-4">
                      <Button 
                        variant="link" 
                        type="button"
                        className="text-botscheme-ocean-blue hover:text-botscheme-royal-purple transition-colors p-0 h-auto"
                        onClick={() => setForgotPasswordOpen(true)}
                      >
                        Forgot your password?
                      </Button>

                      <Button 
                        type="submit" 
                        className="flex-grow bg-gradient-to-r from-botscheme-ocean-blue to-botscheme-royal-purple hover:opacity-90 transition-all shadow-lg" 
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <span className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Signing in...
                          </span>
                        ) : "Sign In"}
                      </Button>
                    </div>
                  </form>
                </Form>
                
                {/* Google OAuth Section - Hidden based on flag */}
                {ENABLE_GOOGLE_AUTH && (
                  <>
                    <div className="relative my-6">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-white/10"></div>
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground rounded">Or continue with</span>
                      </div>
                    </div>
                  
                    <Button 
                      variant="outline" 
                      className="w-full border-white/10 bg-white/5 hover:bg-white/10 transition-all backdrop-blur-sm" 
                      onClick={handleGoogleLogin}
                      disabled={isLoading}
                    >
                      <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                        <path
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          fill="#4285F4"
                        />
                        <path
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          fill="#34A853"
                        />
                        <path
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          fill="#FBBC05"
                        />
                        <path
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          fill="#EA4335"
                        />
                      </svg>
                      Sign in with Google
                    </Button>
                  </>
                )}
                
                <div className="text-center mt-6">
                  <Button 
                    variant="link" 
                    className="text-botscheme-ocean-blue hover:text-botscheme-royal-purple transition-colors"
                    onClick={() => navigate("/auth/register")}
                  >
                    Don't have an account? Sign up
                  </Button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Image/Illustration side with animated elements */}
          <div className="relative h-64 w-full bg-gradient-to-br from-botscheme-royal-purple to-botscheme-ocean-blue lg:h-auto lg:w-1/2 overflow-hidden">
            <div className="absolute inset-0 flex items-center justify-center p-6">
              <div className="text-center text-white max-w-md">
                {/* Animated title */}
                <h2 
                  className={`text-2xl font-bold mb-4 transition-all duration-700 ${
                    animationStage >= 1 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                  }`}
                >
                  Trade Bot 365
                </h2>
                
                {/* Animated typing effect for description */}
                <div className={`min-h-[3rem] transition-opacity duration-500 ${
                  animationStage >= 2 ? "opacity-100" : "opacity-0"
                }`}>
                  {animationStage >= 2 && (
                    <TypingEffect 
                      text="Access your automated trading bots and start maximizing your trading potential with our advanced algorithms."
                      speed={35}
                      className="max-w-md mx-auto opacity-90"
                    />
                  )}
                </div>
                
                {/* Animated chart icons */}
                <div className={`mt-10 transition-all duration-700 ${
                  animationStage >= 3 ? "opacity-100 scale-100" : "opacity-0 scale-90"
                }`}>
                  <div className="inline-flex items-center justify-center p-4 bg-white/10 rounded-full backdrop-blur-sm">
                    {/* Mini chart animation */}
                    <div className="relative h-24 w-24 flex items-center justify-center">
                      {/* Chart line */}
                      {animationStage >= 3 && (
                        <ChartBar className="h-16 w-16 text-white animate-pulse" />
                      )}
                      
                      {/* Trending indicator with animation */}
                      {animationStage >= 3 && (
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                          <TrendingUp className="h-8 w-8 text-botscheme-emerald-green animate-fade-in" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Animated decorative circles with better animations */}
            <div 
              className={`absolute top-5 right-5 h-20 w-20 rounded-full bg-white/10 transition-all duration-1000 ease-in-out ${
                animationStage >= 4 ? "opacity-100" : "opacity-0"
              }`}
              style={{animation: animationStage >= 4 ? "float 6s ease-in-out infinite" : "none"}}
            ></div>
            <div 
              className={`absolute bottom-10 left-10 h-16 w-16 rounded-full bg-white/10 transition-all duration-1000 delay-300 ease-in-out ${
                animationStage >= 4 ? "opacity-100" : "opacity-0"
              }`}
              style={{animation: animationStage >= 4 ? "float 7s ease-in-out infinite" : "none"}}
            ></div>
            <div 
              className={`absolute bottom-20 right-20 h-10 w-10 rounded-full bg-white/10 transition-all duration-1000 delay-500 ease-in-out ${
                animationStage >= 4 ? "opacity-100" : "opacity-0"
              }`}
              style={{animation: animationStage >= 4 ? "float 8s ease-in-out infinite" : "none"}}
            ></div>
          </div>
        </div>
      </div>
      
      {/* Add floating animation keyframes */}
      <style>
        {`
        @keyframes float {
          0% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-15px);
          }
          100% {
            transform: translateY(0px);
          }
        }
        `}
      </style>
      
      {/* Forgot Password Dialog */}
      <ForgotPasswordDialog 
        open={forgotPasswordOpen} 
        onOpenChange={setForgotPasswordOpen} 
      />
    </div>
  );
};

const Login = () => {
  const isMobile = useIsMobile();
  
  // Render mobile or desktop version based on device detection
  return isMobile ? <MobileLoginForm /> : <DesktopLogin />;
};

export default Login;
